<template>
	<div class="container-fluid p-4">
		<div class="d-flex justify-content-between align-items-center mb-4">
			<div>
				<h1 class="mb-0">Custom Mixes</h1>
				<p class="text-muted mb-0">
					Your district's own mixes, built from catalog products and named inert
					carriers.
				</p>
			</div>
		</div>

		<div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>

		<!-- The editor states the whole recipe: a mix is replaced by the rows
		     written here, so every component is always present. -->
		<div class="card">
			<div class="card-body">
				<h2 class="h5 mb-0">
					{{ editingId === null ? "New Mix" : `Edit Mix #${editingId}` }}
				</h2>
				<p class="text-muted mb-3 small">
					At least two components, each with a positive amount and a unit. An
					inert carrier is named rather than drawn from the catalog.
				</p>

				<form @submit.prevent="saveMix">
					<div class="row g-2 align-items-end mb-3">
						<div class="col-12 col-md-4">
							<label class="form-label" for="mixName">Mix name</label>
							<input
								id="mixName"
								v-model="draftName"
								type="text"
								class="form-control"
								placeholder="e.g. Evening Larvicide Blend"
								required
							/>
						</div>
						<div class="col-6 col-md-2">
							<label class="form-label" for="preparedAmount">
								Prepared amount
							</label>
							<input
								id="preparedAmount"
								v-model.number="draftPreparedAmount"
								type="number"
								min="0"
								step="any"
								class="form-control"
								required
							/>
						</div>
						<div class="col-6 col-md-2">
							<label class="form-label" for="preparedUnit">Prepared unit</label>
							<input
								id="preparedUnit"
								v-model="draftPreparedUnit"
								type="text"
								class="form-control"
								placeholder="gal, lb…"
								required
							/>
						</div>
					</div>

					<div
						v-for="(component, index) in draftComponents"
						:key="index"
						class="row g-2 align-items-end mb-2"
					>
						<div class="col-12 col-md-2">
							<label class="form-label" :for="`componentInert${index}`">
								Carrier
							</label>
							<div class="form-check form-switch mt-2">
								<input
									:id="`componentInert${index}`"
									v-model="component.is_inert"
									class="form-check-input"
									type="checkbox"
								/>
								<label class="form-check-label" :for="`componentInert${index}`">
									Inert
								</label>
							</div>
						</div>
						<div class="col-12 col-md-5">
							<label class="form-label" :for="`componentName${index}`">
								{{
									component.is_inert ? "Inert carrier name" : "Catalog product"
								}}
							</label>
							<input
								v-if="component.is_inert"
								:id="`componentName${index}`"
								v-model="component.name"
								type="text"
								class="form-control"
								placeholder="e.g. Water"
							/>
							<select
								v-else
								:id="`componentName${index}`"
								v-model="component.product_catalog_id"
								class="form-select"
							>
								<option value="" disabled>Choose a product</option>
								<option
									v-for="product in catalog"
									:key="product.product_id"
									:value="product.product_id"
								>
									{{ product.name }}
								</option>
							</select>
						</div>
						<div class="col-6 col-md-2">
							<label class="form-label" :for="`componentAmount${index}`">
								Amount
							</label>
							<input
								:id="`componentAmount${index}`"
								v-model.number="component.amount"
								type="number"
								min="0"
								step="any"
								class="form-control"
							/>
						</div>
						<div class="col-6 col-md-2">
							<label class="form-label" :for="`componentUnit${index}`">
								Unit
							</label>
							<input
								:id="`componentUnit${index}`"
								v-model="component.unit"
								type="text"
								class="form-control"
								placeholder="lb, gal…"
							/>
						</div>
						<div class="col-6 col-md-auto">
							<ButtonLoading
								type="button"
								text="Remove"
								icon="bi bi-trash"
								variant="outline-danger"
								size="sm"
								:disabled="draftComponents.length <= 2"
								@click="removeComponent(index)"
							/>
						</div>
					</div>

					<ButtonLoading
						type="button"
						text="Add component"
						icon="bi bi-plus-lg"
						variant="outline-secondary"
						size="sm"
						class="mb-3"
						@click="addComponent"
					/>

					<div class="d-flex align-items-center gap-3">
						<ButtonLoading
							type="submit"
							:text="editingId === null ? 'Create mix' : 'Save mix'"
							icon="bi bi-check2"
							variant="primary"
							:loading="saving"
							:disabled="!draftValid"
						/>
						<ButtonLoading
							v-if="editingId !== null"
							type="button"
							text="Cancel"
							variant="outline-secondary"
							@click="resetDraft"
						/>
					</div>
				</form>
			</div>
		</div>

		<div class="card mt-4">
			<div class="card-body">
				<h2 class="h5 mb-3">Mixes</h2>

				<div v-if="loading" class="text-center py-4">
					<div class="spinner-border text-primary" role="status">
						<span class="visually-hidden">Loading…</span>
					</div>
				</div>

				<div v-else-if="mixes.length === 0" class="text-center py-4 text-muted">
					No custom mixes yet. Build the district's first one above.
				</div>

				<div v-else class="table-responsive">
					<table class="table table-striped align-middle">
						<thead class="table-light">
							<tr>
								<th scope="col">Name</th>
								<th scope="col">Prepared batch</th>
								<th scope="col">Components</th>
								<th scope="col">Status</th>
								<th scope="col" class="text-end">Actions</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="mix in mixes"
								:key="mix.id"
								:class="{ 'text-muted': mix.retired }"
							>
								<td class="fw-semibold">{{ mix.name }}</td>
								<td>
									{{ formatAmount(mix.prepared_amount) }}
									{{ mix.prepared_unit }}
								</td>
								<td>
									<span
										v-for="(component, index) in mix.components"
										:key="component.id"
									>
										<span v-if="index > 0">· </span>
										{{ component.name }}
										<span class="text-muted">
											{{ formatAmount(component.amount) }} {{ component.unit }}
										</span>
										<span
											v-if="component.is_inert"
											class="badge bg-secondary ms-1"
										>
											Inert
										</span>
									</span>
								</td>
								<td>
									<span
										class="badge rounded-pill"
										:class="mix.retired ? 'bg-secondary' : 'bg-success'"
									>
										{{ mix.retired ? "Retired" : "Active" }}
									</span>
								</td>
								<td class="text-end text-nowrap">
									<RouterLink
										class="btn btn-sm btn-outline-secondary me-1"
										:to="`/_/inventory/mixes/${mix.id}`"
									>
										View
									</RouterLink>
									<ButtonLoading
										text="Edit"
										icon="bi bi-pencil"
										variant="outline-secondary"
										size="sm"
										class="me-1"
										@click="startEdit(mix)"
									/>
									<ButtonLoading
										text="Delete"
										icon="bi bi-trash"
										variant="outline-danger"
										size="sm"
										:loading="deletingId === mix.id"
										@click="deleteMix(mix)"
									/>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>

		<ToastNotification
			:title="toastTitle"
			:message="toastMessage"
			:show="toastShow"
			@update:show="toastShow = $event"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";

import { apiClient } from "@/client";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import type {
	InventoryMix,
	MixWrite,
	PesticideCatalogProduct,
} from "@/type/api";

const toastTitle = ref("");
const toastMessage = ref("");
const toastShow = ref(false);

function showNotification(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
}

const mixes = ref<InventoryMix[]>([]);
const catalog = ref<PesticideCatalogProduct[]>([]);
const loading = ref(false);
const error = ref("");

function formatAmount(value: number): string {
	if (Number.isInteger(value)) return String(value);
	return value.toFixed(4).replace(/0+$/, "").replace(/\.$/, "");
}

// A recipe row in progress: an inert row takes a name, a product row a catalog
// slug. Keeping both fields lets a row flip between them without losing input.
interface ComponentDraft {
	is_inert: boolean;
	product_catalog_id: string;
	name: string;
	amount: number;
	unit: string;
}

function emptyComponent(): ComponentDraft {
	return {
		is_inert: false,
		product_catalog_id: "",
		name: "",
		amount: 1,
		unit: "",
	};
}

const editingId = ref<number | null>(null);
const draftName = ref("");
const draftPreparedAmount = ref(0);
const draftPreparedUnit = ref("");
// A mix needs at least two components, so the editor opens with two rows.
const draftComponents = ref<ComponentDraft[]>([
	emptyComponent(),
	emptyComponent(),
]);
const saving = ref(false);
const deletingId = ref<number | null>(null);

const draftValid = computed(() => {
	if (draftName.value.trim() === "") return false;
	if (!(draftPreparedAmount.value > 0)) return false;
	if (draftPreparedUnit.value.trim() === "") return false;
	if (draftComponents.value.length < 2) return false;
	return draftComponents.value.every((component) => {
		if (!(component.amount > 0)) return false;
		if (component.unit.trim() === "") return false;
		if (component.is_inert) return component.name.trim() !== "";
		return component.product_catalog_id !== "";
	});
});

function addComponent() {
	draftComponents.value.push(emptyComponent());
}

function removeComponent(index: number) {
	draftComponents.value.splice(index, 1);
}

function resetDraft() {
	editingId.value = null;
	draftName.value = "";
	draftPreparedAmount.value = 0;
	draftPreparedUnit.value = "";
	draftComponents.value = [emptyComponent(), emptyComponent()];
}

function startEdit(mix: InventoryMix) {
	editingId.value = mix.id;
	draftName.value = mix.name;
	draftPreparedAmount.value = mix.prepared_amount;
	draftPreparedUnit.value = mix.prepared_unit;
	draftComponents.value = mix.components.map((component) => ({
		is_inert: component.is_inert,
		product_catalog_id: component.product_catalog_id ?? "",
		name: component.is_inert ? component.name : "",
		amount: component.amount,
		unit: component.unit,
	}));
}

async function load() {
	loading.value = true;
	error.value = "";
	try {
		// The mixes collection through the generic QUERY endpoint (the legacy GET
		// list's replacement, doc/in-progress/query.md §11 Phase 4), walked whole;
		// the catalog beside it is a different resource's page and keeps its GET.
		const [mixPage, catalogData] = await Promise.all([
			apiClient.JSONQueryAll<InventoryMix>("/api/inventory/mix"),
			apiClient.JSONGet<{ products: PesticideCatalogProduct[]; total: number }>(
				"/api/pesticide-catalog",
			),
		]);
		mixes.value = mixPage.items;
		catalog.value = catalogData.products ?? [];
	} catch (e: any) {
		error.value = e?.message || "Failed to load custom mixes.";
		showNotification("Failed to load mixes", error.value);
	} finally {
		loading.value = false;
	}
}

async function saveMix() {
	if (!draftValid.value) return;
	saving.value = true;
	const body: MixWrite = {
		name: draftName.value.trim(),
		prepared_amount: draftPreparedAmount.value,
		prepared_unit: draftPreparedUnit.value.trim(),
		components: draftComponents.value.map((component) =>
			component.is_inert
				? {
						name: component.name.trim(),
						is_inert: true,
						amount: component.amount,
						unit: component.unit.trim(),
					}
				: {
						product_catalog_id: component.product_catalog_id,
						is_inert: false,
						amount: component.amount,
						unit: component.unit.trim(),
					},
		),
	};
	try {
		if (editingId.value === null) {
			const created = await apiClient.JSONPost<InventoryMix>(
				"/api/inventory/mix",
				body,
			);
			resetDraft();
			await load();
			showNotification("Mix created", `"${created.name}" is ready to use.`);
		} else {
			const updated = await apiClient.JSONPut<InventoryMix>(
				`/api/inventory/mix/${editingId.value}`,
				body,
			);
			resetDraft();
			await load();
			showNotification("Mix saved", `"${updated.name}" is up to date.`);
		}
	} catch (e: any) {
		showNotification("Failed to save mix", e?.message || "Please try again.");
	} finally {
		saving.value = false;
	}
}

async function deleteMix(mix: InventoryMix) {
	if (!confirm(`Delete the mix "${mix.name}"? This cannot be undone.`)) return;
	deletingId.value = mix.id;
	try {
		await apiClient.JSONDelete(`/api/inventory/mix/${mix.id}`);
		mixes.value = mixes.value.filter((row) => row.id !== mix.id);
		if (editingId.value === mix.id) resetDraft();
		showNotification("Mix deleted", `"${mix.name}" is no longer offered.`);
	} catch (e: any) {
		showNotification("Failed to delete mix", e?.message || "Please try again.");
	} finally {
		deletingId.value = null;
	}
}

onMounted(load);
</script>

<style scoped>
.table-responsive {
	overflow-x: auto;
}

th {
	white-space: nowrap;
}
</style>
